/**
 * TPO (Time Price Opportunity) / Market Profile Plugin for LightweightCharts v5
 *
 * Two render modes:
 *   - Histogram: horizontal bars extending LEFT from session anchor (mirrored,
 *     back-to-back with the volume profile which extends right)
 *   - Letters: classic Market Profile with conventional period letters
 *
 * Conventional lettering:
 *   RTH: A-M (uppercase, 9:30-16:00 ET)
 *   Overnight: a-z (lowercase, 18:00-09:30 ET)
 *
 * Features:
 *   - POC (amber), Value Area (teal), IB (blue) highlighting
 *   - Open (green) / Close (red) markers
 *   - Single prints (red)
 *   - Session split: full ETH or overnight + RTH
 *   - Multi-day on-demand loading with visible range subscription
 *   - Real-time SSE updates
 */

import { CONFIG } from '../config';
import { authFetch } from '../auth/authFetch';
import { unifiedSSE } from '../hooks/useUnifiedSSE';

// Resolve a session-anchor timestamp (RTH/Globex open) to an x-coordinate. Exact match
// first, so on a time-uniform chart (where the open is always an exact candle time) the
// behaviour is unchanged. Otherwise snap to the CLOSEST bar, but ONLY when the anchor
// falls strictly BETWEEN existing bars -- the renko case, where bricks form on price
// crossings so there is no brick at exactly 9:30 ET and timeToCoordinate returns null.
// Outside the data range returns null (the original skip). NOTE: renko display times are
// de-collided formation seconds, so "closest" is the nearest brick by display time
// (approximately, not exactly, the 9:30 brick); the plugin only sees series.data().
function anchorCoordinate(chart, series, ts) {
  const exact = chart.timeScale().timeToCoordinate(ts);
  if (exact !== null && exact !== undefined) return exact;
  const data = (series && series.data) ? series.data() : null;
  if (!data || data.length < 2) return null;
  if (ts <= data[0].time || ts >= data[data.length - 1].time) return null;
  let lo = 0, hi = data.length - 1;
  while (lo < hi - 1) { const mid = (lo + hi) >> 1; if (data[mid].time <= ts) lo = mid; else hi = mid; }
  const nearest = (ts - data[lo].time) <= (data[hi].time - ts) ? data[lo].time : data[hi].time;
  return chart.timeScale().timeToCoordinate(nearest);
}

// Extending POC/VAH/VAL line with an inline label at the right end of the line,
// vertically centered on it (OI-levels chip style). Shared by both TPO renderers.
function drawExtendingLine(ctx, series, price, label, color, fromX, toX, dashed, hRatio, vRatio) {
  if (!price) return;
  const y = series.priceToCoordinate(price);
  if (y === null) return;
  const bitmapY = Math.round(y * vRatio);

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  if (dashed) ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(fromX, bitmapY);
  ctx.lineTo(toX, bitmapY);
  ctx.stroke();
  ctx.setLineDash([]);

  const fontSize = Math.round(10 * hRatio);
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  const textWidth = ctx.measureText(label).width;
  const padding = 3 * hRatio;
  const labelX = toX - padding * 2;
  ctx.fillStyle = 'rgba(15, 20, 25, 0.85)';
  ctx.fillRect(
    labelX - textWidth - padding,
    bitmapY - fontSize / 2 - padding,
    textWidth + padding * 2,
    fontSize + padding * 2
  );
  ctx.fillStyle = color;
  ctx.fillText(label, labelX, bitmapY);
  ctx.restore();
}

// Resolve the session-end timestamp to an x-coordinate. Shared by both TPO renderers.
function sessionEndX(chart, series, endTs, chartWidth, hRatio) {
  if (!endTs) return chartWidth;
  // Direct lookup first
  const coord = chart.timeScale().timeToCoordinate(endTs);
  if (coord !== null) return Math.round(coord * hRatio);
  // Interpolate: find the last candle at or before session end
  const data = series.data();
  if (!data || data.length === 0) return chartWidth;
  // Binary search for last timestamp <= endTs
  let lo = 0, hi = data.length - 1;
  if (data[0].time > endTs) return chartWidth;
  // Session end at/beyond the last loaded bar = the forming session: extend to the pane edge
  if (data[hi].time <= endTs) return chartWidth;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (data[mid].time <= endTs) lo = mid; else hi = mid;
  }
  const c = chart.timeScale().timeToCoordinate(data[lo].time);
  return c !== null ? Math.round(c * hRatio) : chartWidth;
}

// ── Adaptive Resolution ─────────────────────────────────────────────────

const PERIOD_ORDER = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLM';
const MIN_ROW_PX = 16;         // minimum pixel height per row before aggregating (matches font size)
const MAX_AGG_FACTOR = 16;     // max ticks per aggregated row
const MAX_VISIBLE_LETTERS = 40; // cap letters per row to prevent wide overflow

// ── Colors ──────────────────────────────────────────────────────────────

const COLORS = {
  poc: 'rgba(251, 191, 36, 0.7)',        // amber
  pocLine: 'rgba(251, 191, 36, 0.5)',
  va: 'rgba(38, 166, 154, 0.45)',         // teal
  vaLine: 'rgba(38, 166, 154, 0.5)',
  vaBg: 'rgba(38, 166, 154, 0.12)',       // subtle VA band for letters
  outside: 'rgba(107, 114, 128, 0.3)',    // gray
  ib: 'rgba(96, 165, 250, 0.5)',          // blue
  ibLine: 'rgba(96, 165, 250, 0.4)',
  singlePrint: 'rgba(239, 68, 68, 0.6)', // red
  openMark: '#00e676',                     // bright green
  closeMark: '#ff1744',                    // bright red
  rthLetter: 'rgba(255, 255, 255, 0.85)',
  overnightLetter: 'rgba(156, 163, 175, 0.6)', // dimmed gray
  outline: 'rgba(255, 255, 255, 0.1)',
  pocBg: 'rgba(251, 191, 36, 0.25)',
};


// ── Histogram Renderer ──────────────────────────────────────────────────

class TPOHistogramRenderer {
  constructor(profileData, series, chart, options = {}) {
    this.profileData = profileData;
    this.series = series;
    this.chart = chart;
    this.options = { histogramWidth: 150, ...options };
  }

  draw(target) {
    if (!this.profileData || !this.series) return;

    const { price_levels, normalized_counts, poc, vah, val,
            single_prints, open_price, close_price, tick_size } = this.profileData;
    const tickSize = tick_size || 0.25;

    if (!price_levels || price_levels.length === 0) return;

    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context;
      const { horizontalPixelRatio, verticalPixelRatio } = scope;

      // Anchor point
      const anchorTs = this.profileData.meta.session_type === 'rth'
        ? this.profileData.rth_open_ts
        : this.profileData.globex_open_ts;
      if (!anchorTs) return;

      const anchorX = anchorCoordinate(this.chart, this.series, anchorTs);
      if (anchorX === null || anchorX === undefined) return;
      const bitmapAnchorX = Math.round(anchorX * horizontalPixelRatio);

      const maxBarWidth = this.options.histogramWidth * horizontalPixelRatio;
      const levelsOnly = this.options.levelsOnly;
      const mirror = !!this.options.mirror;

      if (!levelsOnly) {
        const singlePrintSet = new Set(single_prints || []);

        // Draw bars extending LEFT from anchor when mirrored, RIGHT otherwise
        for (let i = 0; i < price_levels.length; i++) {
          const price = price_levels[i];
          const count = normalized_counts[i];

          const yTop = this.series.priceToCoordinate(price + tickSize * 0.5);
          const yBottom = this.series.priceToCoordinate(price - tickSize * 0.5);
          if (yTop === null || yBottom === null) continue;

          const bitmapYTop = Math.round(yTop * verticalPixelRatio);
          const bitmapYBottom = Math.round(yBottom * verticalPixelRatio);
          const barHeight = Math.max(1, bitmapYBottom - bitmapYTop);
          const barWidth = Math.max(1, (count / 100) * maxBarWidth);

          // Determine color
          let color;
          if (price === poc) {
            color = COLORS.poc;
          } else if (singlePrintSet.has(price)) {
            color = COLORS.singlePrint;
          } else if (price >= val && price <= vah) {
            color = COLORS.va;
          } else {
            color = COLORS.outside;
          }

          const barX = mirror ? bitmapAnchorX - barWidth : bitmapAnchorX;
          ctx.fillStyle = color;
          ctx.fillRect(barX, bitmapYTop, barWidth, barHeight);

          // Outline
          ctx.strokeStyle = COLORS.outline;
          ctx.lineWidth = 1;
          ctx.strokeRect(barX, bitmapYTop, barWidth, barHeight);
        }

        // Open/close markers
        this._drawTriangleMarker(ctx, bitmapAnchorX, open_price, tickSize, maxBarWidth,
          COLORS.openMark, true, horizontalPixelRatio, verticalPixelRatio, mirror);
        this._drawTriangleMarker(ctx, bitmapAnchorX, close_price, tickSize, maxBarWidth,
          COLORS.closeMark, false, horizontalPixelRatio, verticalPixelRatio, mirror);
      }

      // Extending lines (start from right edge of profile, end at session boundary);
      // mirrored profiles end AT the anchor
      if (!this.options.noExtendingLines) {
        const chartWidth = scope.bitmapSize.width;
        const profileRightEdge = mirror ? bitmapAnchorX : bitmapAnchorX + maxBarWidth;
        const lineEndX = sessionEndX(this.chart, this.series, this.profileData.session_end_ts, chartWidth, horizontalPixelRatio);
        drawExtendingLine(ctx, this.series, poc, 'TPO POC', COLORS.pocLine, profileRightEdge, lineEndX, false, horizontalPixelRatio, verticalPixelRatio);
        drawExtendingLine(ctx, this.series, vah, 'TPO VAH', COLORS.vaLine, profileRightEdge, lineEndX, true, horizontalPixelRatio, verticalPixelRatio);
        drawExtendingLine(ctx, this.series, val, 'TPO VAL', COLORS.vaLine, profileRightEdge, lineEndX, true, horizontalPixelRatio, verticalPixelRatio);
      }
    });
  }

  _drawTriangleMarker(ctx, anchorX, price, tickSize, maxBarWidth, color, isOpen, hRatio, vRatio, mirror) {
    if (!price) return;
    const y = this.series.priceToCoordinate(price);
    if (y === null) return;
    const bitmapY = Math.round(y * vRatio);
    const size = 6 * hRatio;
    const dir = mirror ? -1 : 1;

    ctx.fillStyle = color;
    ctx.beginPath();
    if (isOpen) {
      // Triangle at anchor pointing INTO the profile
      const x = anchorX;
      ctx.moveTo(x, bitmapY - size);
      ctx.lineTo(x, bitmapY + size);
      ctx.lineTo(x + dir * size, bitmapY);
    } else {
      // Triangle at far edge of profile pointing back toward the anchor
      const x = anchorX + dir * (maxBarWidth + size);
      ctx.moveTo(x, bitmapY);
      ctx.lineTo(x - dir * size, bitmapY - size);
      ctx.lineTo(x - dir * size, bitmapY + size);
    }
    ctx.closePath();
    ctx.fill();
  }
}


// ── Letter Renderer ─────────────────────────────────────────────────────

class TPOLetterRenderer {
  constructor(profileData, series, chart, options = {}) {
    this.profileData = profileData;
    this.series = series;
    this.chart = chart;
    this.options = { maxLetterColumns: 60, ...options };
  }

  // ── Adaptive resolution helpers ───────────────────────────────────────

  _computeAggFactor(baseRowHeight) {
    let factor = 1;
    while (baseRowHeight * factor < MIN_ROW_PX && factor < MAX_AGG_FACTOR) {
      factor *= 2;
    }
    return factor;
  }

  _priceToBucket(price, aggFactor, tickSize) {
    const bucketSize = aggFactor * tickSize;
    return Math.round(Math.floor(price / bucketSize) * bucketSize * 10000) / 10000;
  }

  _aggregateProfile(profileData, aggFactor, tickSize) {
    const { price_levels, letters_at_level, normalized_counts, single_prints } = profileData;
    const singlePrintSet = new Set(single_prints || []);
    const bucketMap = new Map();

    for (let i = 0; i < price_levels.length; i++) {
      const bucketPrice = this._priceToBucket(price_levels[i], aggFactor, tickSize);

      if (!bucketMap.has(bucketPrice)) {
        bucketMap.set(bucketPrice, { letterSets: [], maxCount: 0, hasSinglePrint: false });
      }

      const bucket = bucketMap.get(bucketPrice);
      bucket.letterSets.push(letters_at_level[i] || '');
      bucket.maxCount = Math.max(bucket.maxCount, normalized_counts[i] || 0);
      if (singlePrintSet.has(price_levels[i])) bucket.hasSinglePrint = true;
    }

    const aggPriceLevels = [];
    const aggLetters = [];
    const aggCounts = [];
    const aggSinglePrints = [];

    const sortedBuckets = [...bucketMap.entries()].sort((a, b) => a[0] - b[0]);

    for (const [bucketPrice, bucket] of sortedBuckets) {
      aggPriceLevels.push(bucketPrice);

      // Merge letters: chronological union of unique letters
      const allLetters = new Set();
      for (const letterStr of bucket.letterSets) {
        for (const ch of letterStr) allLetters.add(ch);
      }
      const merged = [...allLetters]
        .sort((a, b) => PERIOD_ORDER.indexOf(a) - PERIOD_ORDER.indexOf(b))
        .join('');
      aggLetters.push(merged.length > MAX_VISIBLE_LETTERS ? merged.slice(0, MAX_VISIBLE_LETTERS) : merged);

      aggCounts.push(bucket.maxCount);
      if (bucket.hasSinglePrint && merged.length === 1) aggSinglePrints.push(bucketPrice);
    }

    return {
      price_levels: aggPriceLevels,
      letters_at_level: aggLetters,
      normalized_counts: aggCounts,
      single_prints: aggSinglePrints,
      effectiveTickSize: aggFactor * tickSize,
    };
  }

  // ── Draw ──────────────────────────────────────────────────────────────

  draw(target) {
    if (!this.profileData || !this.series) return;

    const { poc, vah, val, open_price, open_period_letter,
            close_price, close_period_letter, tick_size } = this.profileData;
    const tickSize = tick_size || 0.25;

    if (!this.profileData.price_levels || this.profileData.price_levels.length === 0 || !this.profileData.letters_at_level) return;

    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context;
      const { horizontalPixelRatio, verticalPixelRatio } = scope;

      const anchorTs = this.profileData.meta.session_type === 'rth'
        ? this.profileData.rth_open_ts
        : this.profileData.globex_open_ts;
      if (!anchorTs) return;

      const anchorX = anchorCoordinate(this.chart, this.series, anchorTs);
      if (anchorX === null || anchorX === undefined) return;
      const bitmapAnchorX = Math.round(anchorX * horizontalPixelRatio);

      // Measure native tick row height to determine aggregation
      const baseYTop = this.series.priceToCoordinate(this.profileData.price_levels[0] + tickSize * 0.5);
      const baseYBottom = this.series.priceToCoordinate(this.profileData.price_levels[0] - tickSize * 0.5);
      if (baseYTop === null || baseYBottom === null) return;
      const baseRowHeight = Math.abs(Math.round(baseYBottom * verticalPixelRatio) - Math.round(baseYTop * verticalPixelRatio));

      // Aggregate if rows are too small for readable letters
      const aggFactor = this._computeAggFactor(baseRowHeight);
      let drawPriceLevels, drawLetters, drawSinglePrints, effectiveTickSize;

      if (aggFactor > 1) {
        const agg = this._aggregateProfile(this.profileData, aggFactor, tickSize);
        drawPriceLevels = agg.price_levels;
        drawLetters = agg.letters_at_level;
        drawSinglePrints = agg.single_prints;
        effectiveTickSize = agg.effectiveTickSize;
      } else {
        drawPriceLevels = this.profileData.price_levels;
        drawLetters = this.profileData.letters_at_level;
        drawSinglePrints = this.profileData.single_prints || [];
        effectiveTickSize = tickSize;
      }

      // Recalculate row height with effective tick size
      const effYTop = this.series.priceToCoordinate(drawPriceLevels[0] + effectiveTickSize * 0.5);
      const effYBottom = this.series.priceToCoordinate(drawPriceLevels[0] - effectiveTickSize * 0.5);
      if (effYTop === null || effYBottom === null) return;
      const rowHeight = Math.abs(Math.round(effYBottom * verticalPixelRatio) - Math.round(effYTop * verticalPixelRatio));
      const fontSize = 16;
      const charWidth = fontSize * 0.65;

      const singlePrintSet = new Set(drawSinglePrints);

      // Key level bucket mapping
      const pocBucket = aggFactor > 1 ? this._priceToBucket(poc, aggFactor, tickSize) : poc;
      const vahBucket = aggFactor > 1 ? this._priceToBucket(vah, aggFactor, tickSize) : vah;
      const valBucket = aggFactor > 1 ? this._priceToBucket(val, aggFactor, tickSize) : val;
      const openBucket = open_price && aggFactor > 1 ? this._priceToBucket(open_price, aggFactor, tickSize) : open_price;
      const closeBucket = close_price && aggFactor > 1 ? this._priceToBucket(close_price, aggFactor, tickSize) : close_price;

      // Find max letters for width calculation (capped)
      let maxLetters = 0;
      for (const letters of drawLetters) {
        const len = Math.min(letters.length, MAX_VISIBLE_LETTERS);
        if (len > maxLetters) maxLetters = len;
      }

      const levelsOnly = this.options.levelsOnly;
      const mirror = !!this.options.mirror;

      if (!levelsOnly) {
        ctx.font = `${fontSize}px monospace`;
        ctx.textBaseline = 'middle';

        for (let i = 0; i < drawPriceLevels.length; i++) {
          const price = drawPriceLevels[i];
          const letters = drawLetters[i] || '';

          const yT = this.series.priceToCoordinate(price + effectiveTickSize * 0.5);
          const yB = this.series.priceToCoordinate(price - effectiveTickSize * 0.5);
          if (yT === null || yB === null) continue;

          const bitmapYTop = Math.round(yT * verticalPixelRatio);
          const bitmapYBottom = Math.round(yB * verticalPixelRatio);
          const bitmapYMid = (bitmapYTop + bitmapYBottom) / 2;
          const rh = bitmapYBottom - bitmapYTop;

          // Background: POC row or VA band
          const bgWidth = maxLetters * charWidth + 4;
          const bgX = mirror ? bitmapAnchorX - bgWidth : bitmapAnchorX;
          if (price === pocBucket) {
            ctx.fillStyle = COLORS.pocBg;
            ctx.fillRect(bgX, bitmapYTop, bgWidth, rh);
          } else if (price >= valBucket && price <= vahBucket) {
            ctx.fillStyle = COLORS.vaBg;
            ctx.fillRect(bgX, bitmapYTop, bgWidth, rh);
          }

          // Extreme zoom fallback: dots when even aggregated rows are too small
          if (rowHeight < 3) {
            const dotWidth = Math.max(1, letters.length * 2);
            let color = COLORS.outside;
            if (price === pocBucket) color = COLORS.poc;
            else if (singlePrintSet.has(price)) color = COLORS.singlePrint;
            else if (price >= valBucket && price <= vahBucket) color = COLORS.va;
            ctx.fillStyle = color;
            ctx.fillRect(mirror ? bitmapAnchorX - dotWidth : bitmapAnchorX, bitmapYTop, dotWidth, Math.max(1, rh));
            continue;
          }

          // Draw letters in chronological order; mirrored rows are right-aligned
          // at the anchor and grow leftward as periods accumulate
          const visibleCount = Math.min(letters.length, MAX_VISIBLE_LETTERS);
          const isOpenRow = price === openBucket;
          const isCloseRow = price === closeBucket;

          for (let j = 0; j < visibleCount; j++) {
            const ch = letters[j];
            const x = mirror
              ? bitmapAnchorX - (visibleCount - j) * charWidth
              : bitmapAnchorX + j * charWidth;
            const cx = x + charWidth / 2;

            // Draw filled circle around open/close period letters
            if (isOpenRow && ch === open_period_letter) {
              const radius = Math.max(charWidth * 0.55, 6 * horizontalPixelRatio);
              ctx.strokeStyle = COLORS.openMark;
              ctx.lineWidth = 2.5 * horizontalPixelRatio;
              ctx.beginPath();
              ctx.arc(cx, bitmapYMid, radius, 0, Math.PI * 2);
              ctx.stroke();
            } else if (isCloseRow && ch === close_period_letter) {
              const radius = Math.max(charWidth * 0.55, 6 * horizontalPixelRatio);
              ctx.strokeStyle = COLORS.closeMark;
              ctx.lineWidth = 2.5 * horizontalPixelRatio;
              ctx.beginPath();
              ctx.arc(cx, bitmapYMid, radius, 0, Math.PI * 2);
              ctx.stroke();
            }

            // Determine letter color
            let color;
            const isUpperCase = ch >= 'A' && ch <= 'Z';
            const isIB = ch === 'A' || ch === 'B';

            if (isIB && isUpperCase) {
              color = COLORS.ib;
            } else if (singlePrintSet.has(price) && letters.length === 1) {
              color = COLORS.singlePrint;
            } else if (isUpperCase) {
              color = COLORS.rthLetter;
            } else {
              color = COLORS.overnightLetter;
            }

            ctx.fillStyle = color;
            ctx.textAlign = 'center';
            ctx.fillText(ch, cx, bitmapYMid);
          }
        }
      }

      // Extending lines at exact prices (not bucketed); mirrored profiles end AT the anchor
      if (!this.options.noExtendingLines) {
        const chartWidth = scope.bitmapSize.width;
        const profileRightEdge = mirror ? bitmapAnchorX : bitmapAnchorX + maxLetters * charWidth;
        const lineEndX = sessionEndX(this.chart, this.series, this.profileData.session_end_ts, chartWidth, horizontalPixelRatio);
        drawExtendingLine(ctx, this.series, poc, 'TPO POC', COLORS.pocLine, profileRightEdge, lineEndX, false, horizontalPixelRatio, verticalPixelRatio);
        drawExtendingLine(ctx, this.series, vah, 'TPO VAH', COLORS.vaLine, profileRightEdge, lineEndX, true, horizontalPixelRatio, verticalPixelRatio);
        drawExtendingLine(ctx, this.series, val, 'TPO VAL', COLORS.vaLine, profileRightEdge, lineEndX, true, horizontalPixelRatio, verticalPixelRatio);
      }
    });
  }

  _drawTriangleMarker(ctx, anchorX, price, profileWidth, color, isOpen, hRatio, vRatio) {
    if (!price) return;
    const y = this.series.priceToCoordinate(price);
    if (y === null) return;
    const bitmapY = Math.round(y * vRatio);
    const size = 6 * hRatio;

    ctx.fillStyle = color;
    ctx.beginPath();
    if (isOpen) {
      const x = anchorX;
      ctx.moveTo(x, bitmapY - size);
      ctx.lineTo(x, bitmapY + size);
      ctx.lineTo(x + size, bitmapY);
    } else {
      const x = anchorX + profileWidth + size;
      ctx.moveTo(x, bitmapY);
      ctx.lineTo(x - size, bitmapY - size);
      ctx.lineTo(x - size, bitmapY + size);
    }
    ctx.closePath();
    ctx.fill();
  }
}


// ── Primitive View / Primitive (LightweightCharts integration) ──────────

class TPOPrimitiveView {
  constructor(profileData, series, chart, options) {
    this._mode = options.renderMode || 'histogram';
    this._histRenderer = new TPOHistogramRenderer(profileData, series, chart, options);
    this._letterRenderer = new TPOLetterRenderer(profileData, series, chart, options);
  }

  renderer() {
    return this._mode === 'letters' ? this._letterRenderer : this._histRenderer;
  }

  updateProfileData(profileData) {
    this._histRenderer.profileData = profileData;
    this._letterRenderer.profileData = profileData;
  }

  setRenderMode(mode) {
    this._mode = mode;
  }
}

class TPOPrimitive {
  constructor(profileData, series, chart, options) {
    this._view = new TPOPrimitiveView(profileData, series, chart, options);
    this._requestUpdate = null;
    this._rafPending = false;
  }

  attached({ requestUpdate }) {
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._requestUpdate = null;
    this._rafPending = false;
  }

  updateAllViews() {
    return this._view;
  }

  paneViews() {
    return [this._view];
  }

  _scheduleUpdate() {
    if (this._requestUpdate && !this._rafPending) {
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        if (this._requestUpdate) this._requestUpdate();
      });
    }
  }

  updateProfileData(profileData) {
    this._view.updateProfileData(profileData);
    this._scheduleUpdate();
  }

  setRenderMode(mode) {
    this._view.setRenderMode(mode);
    this._scheduleUpdate();
  }
}


// ── Plugin ──────────────────────────────────────────────────────────────

export class TPOPlugin {
  constructor(chart, series, chartContainer) {
    this.chart = chart;
    this.series = series;
    this.chartContainer = chartContainer;

    this.primitives = new Map();       // key -> TPOPrimitive
    this.profileDataByDate = new Map(); // key -> profile data
    this.pendingFetches = new Set();

    this.enabled = false;
    this.sessionScope = 'full';        // 'full' or 'eth-rth'
    this.displayStyle = 'basic';       // 'basic' (stacked) or 'split' (periods)
    this.renderMode = 'histogram';     // 'histogram' or 'letters'

    this.lastFetchParams = null;
    this._sseUnsubs = [];
    this.visibleRangeSubscription = null;
    this.visibleRangeDebounceTimer = null;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  async enable(symbol, startDate, endDate, timeframe = '5min') {
    this.enabled = true;
    this.lastFetchParams = { symbol, startDate, endDate, timeframe };
    this._subscribeToVisibleRangeChanges();
    await this.fetchProfiles(symbol, startDate, endDate, timeframe);
    if (this.profileDataByDate.size > 0) {
      this.draw();
    }
    this._connectSSE();
  }

  disable() {
    this._disconnectSSE();
    this._unsubscribeFromVisibleRangeChanges();
    this.enabled = false;
    this.clear();
    this.profileDataByDate.clear();
    this.pendingFetches.clear();
  }

  isEnabled() { return this.enabled; }

  async update(symbol, startDate, endDate, timeframe = '5min') {
    if (!this.enabled) return;
    this.lastFetchParams = { symbol, startDate, endDate, timeframe };
    await this._fetchVisibleProfiles();
  }

  // ── Render Mode ─────────────────────────────────────────────────────

  setRenderMode(mode) {
    if (mode === this.renderMode) return;
    this.renderMode = mode;
    for (const prim of this.primitives.values()) {
      prim.setRenderMode(mode);
    }
  }

  // ── Session Scope ───────────────────────────────────────────────────

  async setSessionScope(scope) {
    if (scope === this.sessionScope) return;
    this.sessionScope = scope;
    await this._refetchAll();
  }

  // ── Display Style ─────────────────────────────────────────────────

  async setDisplayStyle(style) {
    if (style === this.displayStyle) return;
    this.displayStyle = style;
    await this._refetchAll();
  }

  async _refetchAll() {
    if (this.enabled && this.lastFetchParams) {
      this.clear();
      this.profileDataByDate.clear();
      this.pendingFetches.clear();
      const { symbol, startDate, endDate, timeframe } = this.lastFetchParams;
      await this.fetchProfiles(symbol, startDate, endDate, timeframe);
      if (this.profileDataByDate.size > 0) {
        this.draw();
      }
    }
  }

  // ── Session Types ───────────────────────────────────────────────────

  _getSessionTypes() {
    if (this.sessionScope === 'eth-rth') return ['overnight', 'rth'];
    if (this.sessionScope === 'rth') return ['rth'];
    return ['eth'];
  }

  _profileKey(dateStr, sessionType) {
    return sessionType === 'eth' ? dateStr : `${dateStr}:${sessionType}`;
  }

  // ── Fetch ───────────────────────────────────────────────────────────

  async fetchProfiles(symbol, startDate, endDate, timeframe) {
    await this._fetchVisibleProfiles();
  }

  async _fetchVisibleProfiles() {
    if (!this.enabled || !this.chart) return;

    const visibleDates = this._getVisibleDateRange();
    if (!visibleDates.length) return;

    const sessionTypes = this._getSessionTypes();
    const tasks = [];

    for (const dateStr of visibleDates) {
      for (const st of sessionTypes) {
        const key = this._profileKey(dateStr, st);
        if (this.profileDataByDate.has(key) || this.pendingFetches.has(key)) continue;
        tasks.push({ dateStr, sessionType: st, key });
      }
    }

    if (tasks.length === 0) return;

    const results = await Promise.allSettled(
      tasks.map(t => {
        this.pendingFetches.add(t.key);
        return this._fetchSingleProfile(t.dateStr, t.sessionType)
          .then(data => ({ ...t, data }))
          .finally(() => this.pendingFetches.delete(t.key));
      })
    );

    for (const r of results) {
      if (r.status === 'fulfilled' && r.value.data && r.value.data.price_levels?.length > 0) {
        if (this.displayStyle === 'split') {
          this._splitIntoPeriodsAndDraw(r.value.key, r.value.data);
        } else {
          this.profileDataByDate.set(r.value.key, r.value.data);
          this._createPrimitiveForKey(r.value.key, r.value.data);
        }
      }
    }
  }

  _splitIntoPeriodsAndDraw(baseKey, fullProfile) {
    const { periods, price_levels, letters_at_level, tick_size } = fullProfile;
    if (!periods || periods.length === 0) return;

    for (const period of periods) {
      const letter = period.letter;
      const periodKey = `${baseKey}:period:${letter}`;

      // Build a sub-profile containing only this period's letter at each price level
      const subPriceLevels = [];
      const subLetters = [];
      const subCounts = [];

      for (let i = 0; i < price_levels.length; i++) {
        const letters = letters_at_level[i] || '';
        if (letters.includes(letter)) {
          subPriceLevels.push(price_levels[i]);
          subLetters.push(letter);
          subCounts.push(1);
        }
      }

      if (subPriceLevels.length === 0) continue;

      // Create a minimal profile anchored at this period's start_ts
      // Keep poc/vah/val from full profile for bar/letter coloring
      // Use per-period open/close for markers on each column
      const subProfile = {
        ...fullProfile,
        price_levels: subPriceLevels,
        letters_at_level: subLetters,
        normalized_counts: subCounts.map(() => 100),
        single_prints: fullProfile.single_prints || [],
        // Override anchors so the renderer places this column at the period's time
        globex_open_ts: period.start_ts,
        rth_open_ts: period.start_ts,
        open_price: period.open || 0,
        close_price: period.close || 0,
        open_period_letter: letter,
        close_period_letter: letter,
      };

      this.profileDataByDate.set(periodKey, subProfile);
      this._createPrimitiveForKey(periodKey, subProfile, { noExtendingLines: true, mirror: false });
    }

    // Levels overlay — draws POC/VAH/VAL/IB extending lines using the full
    // profile data, anchored at the session start (matching full/split mode)
    const levelsKey = `${baseKey}:period:_levels`;
    this.profileDataByDate.set(levelsKey, fullProfile);
    this._createPrimitiveForKey(levelsKey, fullProfile, { levelsOnly: true, mirror: false });
  }

  async _fetchSingleProfile(dateStr, sessionType) {
    try {
      const url = `${CONFIG.apiUrl}/api/indicators/tpo-profile?symbol=ES&date=${dateStr}&session_type=${sessionType}`;
      const resp = await authFetch(url);
      if (!resp.ok) return null;
      const data = await resp.json();
      return data;
    } catch (e) {
      console.warn('[TPO] Fetch failed:', dateStr, sessionType, e.message);
      return null;
    }
  }

  // ── Visible Range ───────────────────────────────────────────────────

  _getVisibleDateRange() {
    try {
      const range = this.chart.timeScale().getVisibleLogicalRange();
      if (!range) return [];

      const barsInfo = this.series.barsInLogicalRange(range);
      if (!barsInfo) return [];

      const from = barsInfo.from;
      const to = barsInfo.to;
      if (!from || !to) return [];

      // Generate trading days in range
      const dates = [];
      const startDate = new Date(typeof from === 'number' ? from * 1000 : from);
      const endDate = new Date(typeof to === 'number' ? to * 1000 : to);

      // Extend range slightly for profiles anchored at globex open
      startDate.setDate(startDate.getDate() - 1);
      endDate.setDate(endDate.getDate() + 1);

      const current = new Date(startDate);
      while (current <= endDate) {
        if (current.getDay() !== 0 && current.getDay() !== 6) {
          dates.push(current.toISOString().split('T')[0]);
        }
        current.setDate(current.getDate() + 1);
      }

      return dates;
    } catch {
      return [];
    }
  }

  _subscribeToVisibleRangeChanges() {
    if (this.visibleRangeSubscription) return;
    const handler = () => {
      if (this.visibleRangeDebounceTimer) clearTimeout(this.visibleRangeDebounceTimer);
      this.visibleRangeDebounceTimer = setTimeout(() => {
        this._fetchVisibleProfiles();
      }, 300);
    };
    this.chart.timeScale().subscribeVisibleLogicalRangeChange(handler);
    this.visibleRangeSubscription = handler;
  }

  _unsubscribeFromVisibleRangeChanges() {
    if (this.visibleRangeSubscription) {
      this.chart.timeScale().unsubscribeVisibleLogicalRangeChange(this.visibleRangeSubscription);
      this.visibleRangeSubscription = null;
    }
    if (this.visibleRangeDebounceTimer) {
      clearTimeout(this.visibleRangeDebounceTimer);
      this.visibleRangeDebounceTimer = null;
    }
  }

  // ── SSE ─────────────────────────────────────────────────────────────

  _connectSSE() {
    const handleUpdate = (data) => this._handleProfileUpdate(data);
    this._sseUnsubs.push(unifiedSSE.subscribe('tpo-profile', 'tpo_profile_snapshot', handleUpdate));
    this._sseUnsubs.push(unifiedSSE.subscribe('tpo-profile', 'tpo_profile_update', handleUpdate));
  }

  _disconnectSSE() {
    for (const unsub of this._sseUnsubs) {
      if (typeof unsub === 'function') unsub();
    }
    this._sseUnsubs = [];
  }

  _handleProfileUpdate(data) {
    if (!this.enabled || !data || !data.price_levels) return;

    const sessionType = data.meta?.session_type;
    const dateStr = data.meta?.date;
    if (!dateStr) return;

    // Filter by current session mode
    const sessionTypes = this._getSessionTypes();
    if (!sessionTypes.includes(sessionType)) return;

    if (this.displayStyle === 'split') {
      // Clear old period primitives for this date, then re-split
      for (const [k, prim] of this.primitives.entries()) {
        if (k.startsWith(dateStr) && k.includes(':period:')) {
          try { this.series.detachPrimitive(prim); } catch (e) { /* ignore */ }
          this.primitives.delete(k);
          this.profileDataByDate.delete(k);
        }
      }
      this._splitIntoPeriodsAndDraw(this._profileKey(dateStr, sessionType), data);
      return;
    }

    const key = this._profileKey(dateStr, sessionType);
    this.profileDataByDate.set(key, data);

    if (this.primitives.has(key)) {
      this.primitives.get(key).updateProfileData(data);
    } else {
      this._createPrimitiveForKey(key, data);
    }
  }

  // ── Drawing ─────────────────────────────────────────────────────────

  draw() {
    for (const [key, data] of this.profileDataByDate.entries()) {
      if (!this.primitives.has(key)) {
        this._createPrimitiveForKey(key, data);
      }
    }
  }

  _createPrimitiveForKey(key, data, extraOptions = {}) {
    if (this.primitives.has(key)) {
      this.primitives.get(key).updateProfileData(data);
      return;
    }

    const prim = new TPOPrimitive(data, this.series, this.chart, {
      renderMode: this.renderMode,
      mirror: true,
      ...extraOptions,
    });
    this.series.attachPrimitive(prim);
    this.primitives.set(key, prim);
  }

  clear() {
    for (const prim of this.primitives.values()) {
      try { this.series.detachPrimitive(prim); } catch (e) { /* ignore */ }
    }
    this.primitives.clear();
  }
}
